import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Stack,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  alpha,
  Paper,
  IconButton,
  Button,
  Collapse,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  LinearProgress,
} from '@mui/material';
import { 
  School, 
  TrendingUp, 
  TrendingDown, 
  Star,
  EmojiEvents,
  Assignment,
  BarChart,
  ExpandMore,
  ExpandLess,
  Send,
  Close,
  PlayArrow,
  AutoAwesome,
  Warning,
  CheckCircle,
  Cancel,
  Psychology,
} from '@mui/icons-material';

const CompleteTeacherDashboard = () => {
  const [selectedClass, setSelectedClass] = useState('09-10');
  const [selectedSubject, setSelectedSubject] = useState('Physics');
  const [expandedCard, setExpandedCard] = useState(null);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [openTestDialog, setOpenTestDialog] = useState(false);
  const [testType, setTestType] = useState('individual');
  const [selectedPerformanceType, setSelectedPerformanceType] = useState([]);
  const [weekFilter, setWeekFilter] = useState('4week');
  const [expandedStudentTests, setExpandedStudentTests] = useState(null);
  const [selectedStudentForConcept, setSelectedStudentForConcept] = useState(null);

  const classGroups = ['06-08', '09-10', '11-12'];
  const subjects = ['Physics', 'Chemistry', 'Mathematics', 'Biology'];

  // Subject-specific data
  const subjectData = {
    Physics: {
      strongTopics: [
        { name: 'Newton\'s Laws of Motion', avgScore: 92, students: 28 },
        { name: 'Work, Energy & Power', avgScore: 89, students: 27 },
        { name: 'Electromagnetic Induction', avgScore: 91, students: 26 },
        { name: 'Optics - Reflection', avgScore: 90, students: 29 },
        { name: 'Thermodynamics - First Law', avgScore: 87, students: 24 },
        { name: 'Circular Motion', avgScore: 89, students: 26 },
        { name: 'Electrostatics', avgScore: 88, students: 27 },
        { name: 'Modern Physics - Atoms', avgScore: 88, students: 25 },
      ],
      averageTopics: [
        { name: 'Rotational Motion', avgScore: 75, students: 22 },
        { name: 'Waves - Sound', avgScore: 73, students: 23 },
        { name: 'Heat Transfer', avgScore: 76, students: 21 },
        { name: 'Current Electricity', avgScore: 74, students: 24 },
        { name: 'Magnetic Effects', avgScore: 72, students: 20 },
        { name: 'Ray Optics', avgScore: 75, students: 23 },
        { name: 'Gravitation', avgScore: 71, students: 22 },
        { name: 'Elasticity', avgScore: 73, students: 21 },
        { name: 'Fluid Mechanics', avgScore: 74, students: 22 },
        { name: 'SHM - Springs', avgScore: 72, students: 20 },
        { name: 'AC Circuits', avgScore: 76, students: 23 },
        { name: 'Wave Optics', avgScore: 73, students: 21 },
      ],
      focusTopics: [
        { name: 'Semiconductor Devices', avgScore: 58, students: 18 },
        { name: 'Communication Systems', avgScore: 62, students: 19 },
        { name: 'Nuclear Physics', avgScore: 60, students: 17 },
        { name: 'AC RLC Circuits', avgScore: 59, students: 18 },
        { name: 'Dual Nature of Matter', avgScore: 61, students: 19 },
      ],
      tests: [
        { id: 'test01', name: 'Newton\'s Laws', topic: 'Mechanics', subtopic: 'Newton\'s Laws', date: '2024-01-15' },
        { id: 'test02', name: 'Work Energy Power', topic: 'Mechanics', subtopic: 'Work & Energy', date: '2024-01-18' },
        { id: 'test03', name: 'EM Induction', topic: 'Electricity', subtopic: 'EM Induction', date: '2024-01-22' },
        { id: 'test04', name: 'Semiconductors', topic: 'Electronics', subtopic: 'Semiconductors', date: '2024-01-25' },
        { id: 'test05', name: 'Optics Reflection', topic: 'Optics', subtopic: 'Reflection', date: '2024-01-28' },
        { id: 'test06', name: 'Thermodynamics', topic: 'Thermodynamics', subtopic: 'First Law', date: '2024-02-01' },
        { id: 'test07', name: 'Current Electricity', topic: 'Electricity', subtopic: 'Current', date: '2024-02-05' },
        { id: 'test08', name: 'Nuclear Physics', topic: 'Modern Physics', subtopic: 'Nuclear', date: '2024-02-08' },
      ]
    },
    Chemistry: {
      strongTopics: [
        { name: 'Chemical Bonding', avgScore: 90, students: 29 },
        { name: 'Thermochemistry', avgScore: 88, students: 27 },
        { name: 'Solutions', avgScore: 89, students: 28 },
        { name: 'Redox Reactions', avgScore: 87, students: 26 },
        { name: 'Periodic Table', avgScore: 91, students: 30 },
        { name: 'Coordination Compounds', avgScore: 85, students: 25 },
        { name: 'Hydrocarbons', avgScore: 86, students: 27 },
        { name: 'Aldehydes & Ketones', avgScore: 84, students: 24 },
      ],
      averageTopics: [
        { name: 'Chemical Kinetics', avgScore: 74, students: 23 },
        { name: 'Electrochemistry', avgScore: 72, students: 22 },
        { name: 'Surface Chemistry', avgScore: 75, students: 24 },
        { name: 'Haloalkanes', avgScore: 73, students: 21 },
        { name: 'Alcohols & Phenols', avgScore: 76, students: 23 },
        { name: 'Amines', avgScore: 71, students: 20 },
        { name: 'Biomolecules', avgScore: 74, students: 22 },
        { name: 'd-block Elements', avgScore: 72, students: 21 },
        { name: 'p-block Elements', avgScore: 75, students: 23 },
        { name: 'Polymers', avgScore: 73, students: 22 },
        { name: 'Solid State', avgScore: 74, students: 21 },
        { name: 'Ionic Equilibrium', avgScore: 72, students: 20 },
      ],
      focusTopics: [
        { name: 'Metallurgy', avgScore: 59, students: 18 },
        { name: 'Organic Chemistry Reactions', avgScore: 61, students: 19 },
        { name: 'Chemical Equilibrium', avgScore: 58, students: 17 },
        { name: 'Isomerism', avgScore: 60, students: 18 },
        { name: 'Qualitative Analysis', avgScore: 62, students: 19 },
      ],
      tests: [
        { id: 'test01', name: 'Chemical Bonding', topic: 'Structure', subtopic: 'Bonding', date: '2024-01-15' },
        { id: 'test02', name: 'Thermochemistry', topic: 'Physical Chem', subtopic: 'Thermo', date: '2024-01-18' },
        { id: 'test03', name: 'Solutions', topic: 'Physical Chem', subtopic: 'Solutions', date: '2024-01-22' },
        { id: 'test04', name: 'Redox Reactions', topic: 'Physical Chem', subtopic: 'Redox', date: '2024-01-25' },
        { id: 'test05', name: 'Periodic Table', topic: 'Inorganic', subtopic: 'Periodicity', date: '2024-01-28' },
        { id: 'test06', name: 'Hydrocarbons', topic: 'Organic', subtopic: 'Hydrocarbons', date: '2024-02-01' },
        { id: 'test07', name: 'Kinetics', topic: 'Physical Chem', subtopic: 'Kinetics', date: '2024-02-05' },
        { id: 'test08', name: 'Aldehydes', topic: 'Organic', subtopic: 'Aldehydes', date: '2024-02-08' },
      ]
    },
    Mathematics: {
      strongTopics: [
        { name: 'Differentiation', avgScore: 93, students: 30 },
        { name: 'Integration', avgScore: 90, students: 28 },
        { name: 'Matrices', avgScore: 91, students: 29 },
        { name: 'Determinants', avgScore: 89, students: 27 },
        { name: 'Trigonometry', avgScore: 88, students: 26 },
        { name: 'Probability', avgScore: 87, students: 25 },
        { name: 'Vectors', avgScore: 90, students: 28 },
        { name: '3D Geometry', avgScore: 86, students: 26 },
      ],
      averageTopics: [
        { name: 'Complex Numbers', avgScore: 75, students: 23 },
        { name: 'Limits', avgScore: 74, students: 22 },
        { name: 'Continuity', avgScore: 73, students: 21 },
        { name: 'Quadratic Equations', avgScore: 76, students: 24 },
        { name: 'Sequences & Series', avgScore: 72, students: 20 },
        { name: 'Binomial Theorem', avgScore: 75, students: 23 },
        { name: 'Permutations', avgScore: 74, students: 22 },
        { name: 'Combinations', avgScore: 73, students: 21 },
        { name: 'Straight Lines', avgScore: 76, students: 24 },
        { name: 'Circles', avgScore: 72, students: 20 },
        { name: 'Parabola', avgScore: 74, students: 22 },
        { name: 'Relations & Functions', avgScore: 75, students: 23 },
      ],
      focusTopics: [
        { name: 'Differential Equations', avgScore: 60, students: 18 },
        { name: 'Ellipse & Hyperbola', avgScore: 58, students: 17 },
        { name: 'Mathematical Reasoning', avgScore: 61, students: 19 },
        { name: 'Linear Programming', avgScore: 59, students: 18 },
        { name: 'Application of Integrals', avgScore: 62, students: 19 },
      ],
      tests: [
        { id: 'test01', name: 'Differentiation', topic: 'Calculus', subtopic: 'Derivatives', date: '2024-01-15' },
        { id: 'test02', name: 'Integration', topic: 'Calculus', subtopic: 'Integrals', date: '2024-01-18' },
        { id: 'test03', name: 'Matrices', topic: 'Algebra', subtopic: 'Matrices', date: '2024-01-22' },
        { id: 'test04', name: 'Determinants', topic: 'Algebra', subtopic: 'Determinants', date: '2024-01-25' },
        { id: 'test05', name: 'Trigonometry', topic: 'Trigonometry', subtopic: 'Functions', date: '2024-01-28' },
        { id: 'test06', name: 'Probability', topic: 'Statistics', subtopic: 'Probability', date: '2024-02-01' },
        { id: 'test07', name: 'Vectors', topic: 'Geometry', subtopic: 'Vectors', date: '2024-02-05' },
        { id: 'test08', name: 'Differential Eq', topic: 'Calculus', subtopic: 'DE', date: '2024-02-08' },
      ]
    },
    Biology: {
      strongTopics: [
        { name: 'Cell Structure', avgScore: 91, students: 29 },
        { name: 'Photosynthesis', avgScore: 89, students: 28 },
        { name: 'Respiration', avgScore: 90, students: 27 },
        { name: 'Human Physiology', avgScore: 88, students: 26 },
        { name: 'Genetics - Mendel', avgScore: 87, students: 25 },
        { name: 'Evolution', avgScore: 86, students: 24 },
        { name: 'Plant Anatomy', avgScore: 88, students: 27 },
        { name: 'Reproduction', avgScore: 89, students: 28 },
      ],
      averageTopics: [
        { name: 'Molecular Basis', avgScore: 74, students: 22 },
        { name: 'Biotechnology', avgScore: 73, students: 21 },
        { name: 'Ecology', avgScore: 75, students: 23 },
        { name: 'Biodiversity', avgScore: 72, students: 20 },
        { name: 'Biomolecules', avgScore: 76, students: 24 },
        { name: 'Neural System', avgScore: 71, students: 20 },
        { name: 'Endocrine System', avgScore: 74, students: 22 },
        { name: 'Immune System', avgScore: 73, students: 21 },
        { name: 'Plant Growth', avgScore: 75, students: 23 },
        { name: 'Mineral Nutrition', avgScore: 72, students: 21 },
        { name: 'Microbes', avgScore: 74, students: 22 },
        { name: 'Animal Kingdom', avgScore: 73, students: 21 },
      ],
      focusTopics: [
        { name: 'DNA Replication', avgScore: 60, students: 18 },
        { name: 'Genetic Engineering', avgScore: 59, students: 17 },
        { name: 'Molecular Genetics', avgScore: 61, students: 19 },
        { name: 'Environmental Issues', avgScore: 58, students: 18 },
        { name: 'Human Health', avgScore: 62, students: 19 },
      ],
      tests: [
        { id: 'test01', name: 'Cell Structure', topic: 'Cell Biology', subtopic: 'Cell', date: '2024-01-15' },
        { id: 'test02', name: 'Photosynthesis', topic: 'Plant Physiology', subtopic: 'Photos', date: '2024-01-18' },
        { id: 'test03', name: 'Respiration', topic: 'Plant Physiology', subtopic: 'Respiration', date: '2024-01-22' },
        { id: 'test04', name: 'Human Physiology', topic: 'Human Bio', subtopic: 'Physiology', date: '2024-01-25' },
        { id: 'test05', name: 'Genetics', topic: 'Genetics', subtopic: 'Mendel', date: '2024-01-28' },
        { id: 'test06', name: 'Evolution', topic: 'Evolution', subtopic: 'Theory', date: '2024-02-01' },
        { id: 'test07', name: 'Biotechnology', topic: 'Biotech', subtopic: 'Principles', date: '2024-02-05' },
        { id: 'test08', name: 'Ecology', topic: 'Ecology', subtopic: 'Ecosystem', date: '2024-02-08' },
      ]
    }
  };

  // Get current subject data
  const currentSubjectData = subjectData[selectedSubject];

  const topSummaryCards = [
    { 
      label: 'Strong Topics', 
      value: currentSubjectData.strongTopics.length, 
      color: '#22c55e', 
      icon: Star,
      topics: currentSubjectData.strongTopics
    },
    { 
      label: 'Average Topics', 
      value: currentSubjectData.averageTopics.length, 
      color: '#f59e0b', 
      icon: BarChart,
      topics: currentSubjectData.averageTopics
    },
    { 
      label: 'Focus Area Topics', 
      value: currentSubjectData.focusTopics.length, 
      color: '#ef4444', 
      icon: TrendingDown,
      topics: currentSubjectData.focusTopics
    },
  ];

  // Teaching Plans
  const teachingPlans = {
    '1week': {
      weeks: [{
        week: 1,
        title: 'Week 1: Quick Review & Practice',
        revise: ['Key concepts revision', 'Formula consolidation', 'Previous weak areas'],
        practice: ['NCERT Back Exercise (20 problems)', 'NCERT Exemplar (15 MCQs)', 'Previous Board Questions (10 questions)'],
        teach: []
      }]
    },
    '2week': {
      weeks: [
        {
          week: 1,
          title: 'Week 1: Foundation Revision',
          revise: ['Core concepts', 'Fundamental principles', 'Important formulas'],
          practice: ['NCERT Exercise (15 problems)', 'Board Previous Year (10 MCQs)', 'NCERT Exemplar'],
          teach: []
        },
        {
          week: 2,
          title: 'Week 2: Advanced Practice',
          revise: ['Complex topics', 'Advanced concepts', 'Integration practice'],
          practice: ['NCERT Back Exercise (20 problems)', 'Board Questions (15 MCQs)', 'NCERT Exemplar (25 problems)'],
          teach: []
        }
      ]
    },
    '4week': {
      weeks: [
        {
          week: 1,
          title: 'Week 1: Foundation Building',
          revise: ['Basic concepts', 'Core fundamentals', 'Chapter-wise basics'],
          practice: ['NCERT Back Exercise (15 problems)', 'NCERT Exemplar (12 MCQs)', 'Previous Board Questions (8 questions)'],
          teach: []
        },
        {
          week: 2,
          title: 'Week 2: Concept Deepening',
          revise: ['Advanced topics', 'Complex theories', 'Application concepts'],
          practice: ['NCERT Exercise (18 problems)', 'Board Previous Year (15 MCQs)', 'NCERT Exemplar Set-1 (20 questions)'],
          teach: []
        },
        {
          week: 3,
          title: 'Week 3: Application & Integration',
          revise: ['Problem-solving techniques', 'Mixed concepts', 'Application areas'],
          practice: ['NCERT Back Exercise (20 problems)', 'Board Questions (15 MCQs)', 'NCERT Exemplar Mixed (25 questions)'],
          teach: []
        },
        {
          week: 4,
          title: 'Week 4: Mastery & Assessment',
          revise: ['Complete revision', 'Quick recap', 'Formula consolidation'],
          practice: ['Full-Length CBSE Paper - 70 marks', 'Previous 3 Years Board Questions', 'NCERT Exemplar Tests (3 tests)', 'Weak Area Practice (30 problems)'],
          teach: []
        }
      ]
    }
  };

  // Generate 32 students
  const generateStudents = () => {
    const names = [
      'Aarav Kumar', 'Ishita Sharma', 'Rohan Patel', 'Ananya Singh', 'Vihaan Reddy',
      'Diya Gupta', 'Aditya Joshi', 'Saanvi Iyer', 'Arjun Nair', 'Myra Desai',
      'Kabir Mehta', 'Aanya Rao', 'Vivaan Das', 'Kiara Bose', 'Reyansh Pillai',
      'Navya Krishnan', 'Atharv Menon', 'Ira Khanna', 'Shivansh Agarwal', 'Anika Verma',
      'Ayaan Shah', 'Pari Bansal', 'Dhruv Malhotra', 'Mira Chopra', 'Arnav Sethi',
      'Zara Ali', 'Aadhya Kapoor', 'Kian Roy', 'Riya Sinha', 'Advait Chatterjee',
      'Prisha Menon', 'Ritvik Das'
    ];

    return names.map((name, index) => {
      const score = 95 - (index * 2) - Math.floor(Math.random() * 5);
      let grade, category;
      
      if (score >= 90) { grade = 'A+'; category = 'Excellent'; }
      else if (score >= 80) { grade = 'A'; category = 'Excellent'; }
      else if (score >= 75) { grade = 'B+'; category = 'Good'; }
      else if (score >= 70) { grade = 'B'; category = 'Average'; }
      else if (score >= 60) { grade = 'C+'; category = 'Average'; }
      else { grade = 'C'; category = 'Focus'; }

      const testsCompleted = Math.max(5, Math.floor(12 - index * 0.3));
      const attemptedTests = currentSubjectData.tests.slice(0, testsCompleted).map(t => t.id);
      const testScores = {};
      attemptedTests.forEach(testId => {
        testScores[testId] = Math.max(50, Math.min(100, score + Math.floor(Math.random() * 15) - 7));
      });

      return {
        id: `student${String(index + 1).padStart(2, '0')}`,
        rollNo: `R${String(index + 1).padStart(3, '0')}`,
        name,
        strongTopics: currentSubjectData.strongTopics.slice(0, Math.max(1, 4 - Math.floor(index / 8))).map(t => t.name.split(' ')[0]),
        averageSubtopics: currentSubjectData.averageTopics.slice(0, Math.max(0, Math.floor(index / 10) + 1)).map(t => t.name.split(' ')[0]),
        focusAreas: score < 75 ? currentSubjectData.focusTopics.slice(0, Math.max(1, Math.floor((75 - score) / 10))).map(t => t.name.split(' ')[0]) : [],
        avgGrade: grade,
        gradeCategory: category,
        overallScore: score,
        testsCompleted,
        attemptedTests,
        testScores,
        conceptMastery: currentSubjectData.tests.slice(0, Math.min(5, testsCompleted)).map((test, i) => ({
          topic: test.topic,
          subtopic: test.subtopic,
          conceptsRead: Math.floor(5 + Math.random() * 8),
          testsGiven: attemptedTests.includes(test.id) ? Math.floor(1 + Math.random() * 2) : 0,
          avgScore: attemptedTests.includes(test.id) ? testScores[test.id] : 0
        }))
      };
    });
  };

  const students = generateStudents();

  const classCategories = {
    excellent: students.filter(s => s.gradeCategory === 'Excellent'),
    good: students.filter(s => s.gradeCategory === 'Good'),
    average: students.filter(s => s.gradeCategory === 'Average'),
    focus: students.filter(s => s.gradeCategory === 'Focus'),
  };

  const handleCardExpand = (index) => {
    setExpandedCard(expandedCard === index ? null : index);
  };

  const handleStudentSelect = (studentId) => {
    setSelectedStudents(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleSelectAllStudents = () => {
    if (selectedStudents.length === students.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(students.map(s => s.id));
    }
  };

  const handleOpenTestDialog = (type, category = 'all', studentIds = []) => {
    setTestType(type);
    if (studentIds.length > 0) {
      setSelectedStudents(studentIds);
    }
    setOpenTestDialog(true);
  };

  const handleGenerateTest = () => {
    const selectedStudentData = students.filter(s => selectedStudents.includes(s.id));
    console.log('Generating test for:', {
      type: testType,
      performanceTypes: selectedPerformanceType,
      students: selectedStudentData
    });
    setOpenTestDialog(false);
    setSelectedStudents([]);
    setSelectedPerformanceType([]);
  };

  const handleStudentRowClick = (studentId) => {
    setExpandedStudentTests(expandedStudentTests === studentId ? null : studentId);
  };

  const getGradeColor = (grade) => {
    if (grade.startsWith('A')) return { bg: '#dcfce7', text: '#166534' };
    if (grade.startsWith('B')) return { bg: '#dbeafe', text: '#1e40af' };
    if (grade.startsWith('C')) return { bg: '#fee2e2', text: '#991b1b' };
    return { bg: '#fef3c7', text: '#92400e' };
  };

  const getInitials = (name) => name.split(' ').map(n => n[0]).join('').toUpperCase();

  const getAvatarColor = (index) => {
    const colors = [
      { bg: '#fee2e2', text: '#991b1b' },
      { bg: '#dbeafe', text: '#1e40af' },
      { bg: '#dcfce7', text: '#166534' },
      { bg: '#fef3c7', text: '#92400e' },
      { bg: '#f3e8ff', text: '#6b21a8' },
      { bg: '#fce7f3', text: '#9f1239' },
    ];
    return colors[index % colors.length];
  };

  const currentPlan = teachingPlans[weekFilter];

  return (
    <Box sx={{ p: 2, minHeight: '100vh', bgcolor: '#f8fafc' }}>
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          <Box>
            <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#1a1a1a', mb: 0.5 }}>
              Teacher Dashboard
            </Typography>
            <Typography sx={{ fontSize: 13, color: '#64748b' }}>
              Complete overview of class performance and teaching plan
            </Typography>
          </Box>

          <Stack direction="row" spacing={2}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel sx={{ fontSize: 13 }}>Class</InputLabel>
              <Select value={selectedClass} label="Class" onChange={(e) => setSelectedClass(e.target.value)} sx={{ fontSize: 13 }}>
                {classGroups.map((group) => <MenuItem key={group} value={group} sx={{ fontSize: 13 }}>{group}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel sx={{ fontSize: 13 }}>Subject</InputLabel>
              <Select value={selectedSubject} label="Subject" onChange={(e) => setSelectedSubject(e.target.value)} sx={{ fontSize: 13 }}>
                {subjects.map((subject) => <MenuItem key={subject} value={subject} sx={{ fontSize: 13 }}>{subject}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>
        </Box>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          {topSummaryCards.map((card, index) => (
            <Grid item xs={12} sm={4} key={index}>
              <Card sx={{ bgcolor: '#fff', borderRadius: 2, boxShadow: 1 }}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }} onClick={() => handleCardExpand(index)}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ bgcolor: alpha(card.color, 0.1), borderRadius: 2, p: 1 }}>
                        <card.icon sx={{ fontSize: 24, color: card.color }} />
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', mb: 0.5 }}>{card.label}</Typography>
                        <Typography sx={{ fontSize: 28, fontWeight: 700, color: card.color }}>{card.value}</Typography>
                      </Box>
                    </Box>
                    <IconButton size="small">{expandedCard === index ? <ExpandLess /> : <ExpandMore />}</IconButton>
                  </Box>
                  <Collapse in={expandedCard === index}>
                    <Box sx={{ mt: 2, maxHeight: 220, overflowY: 'auto' }}>
                      <Stack spacing={1}>
                        {card.topics.map((topic, idx) => (
                          <Paper key={idx} sx={{ p: 1.2, bgcolor: alpha(card.color, 0.05), borderRadius: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#1a1a1a' }}>{topic.name}</Typography>
                              <Chip label={`${topic.avgScore}%`} size="small" sx={{ fontSize: 10, fontWeight: 600, bgcolor: card.color, color: '#fff' }} />
                            </Box>
                            <Typography sx={{ fontSize: 11, color: '#64748b', mt: 0.5 }}>{topic.students} students</Typography>
                          </Paper>
                        ))}
                      </Stack>
                    </Box>
                  </Collapse>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Card sx={{ bgcolor: '#fff', borderRadius: 2, mb: 2, boxShadow: 1 }}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Assignment sx={{ fontSize: 22, color: '#7c3aed' }} />
                <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#1a1a1a' }}>
                  Teaching Plan (Exam Preparation)
                </Typography>
              </Box>
              <ToggleButtonGroup value={weekFilter} exclusive onChange={(e, val) => val && setWeekFilter(val)} size="small">
                <ToggleButton value="1week" sx={{ fontSize: 11, px: 2, textTransform: 'none' }}>1 Week</ToggleButton>
                <ToggleButton value="2week" sx={{ fontSize: 11, px: 2, textTransform: 'none' }}>2 Weeks</ToggleButton>
                <ToggleButton value="4week" sx={{ fontSize: 11, px: 2, textTransform: 'none' }}>4 Weeks</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Stack spacing={1.5}>
              {currentPlan.weeks.map((week, idx) => (
                <Paper key={idx} sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 1 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a', mb: 1 }}>{week.title}</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ bgcolor: alpha('#f59e0b', 0.05), p: 1.2, borderRadius: 1 }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#1a1a1a', mb: 0.8 }}>🔄 Revise</Typography>
                        <Stack spacing={0.5}>
                          {week.revise.map((item, i) => (
                            <Typography key={i} sx={{ fontSize: 11, color: '#374151', pl: 1.5 }}>• {item}</Typography>
                          ))}
                        </Stack>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ bgcolor: alpha('#3b82f6', 0.05), p: 1.2, borderRadius: 1 }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#1a1a1a', mb: 0.8 }}>✍️ Practice</Typography>
                        <Stack spacing={0.5}>
                          {week.practice.map((item, i) => (
                            <Typography key={i} sx={{ fontSize: 11, color: '#374151', pl: 1.5 }}>• {item}</Typography>
                          ))}
                        </Stack>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ bgcolor: alpha('#94a3b8', 0.05), p: 1.2, borderRadius: 1 }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#1a1a1a', mb: 0.8 }}>📚 Teach (New)</Typography>
                        {week.teach.length > 0 ? (
                          <Stack spacing={0.5}>
                            {week.teach.map((item, i) => (
                              <Typography key={i} sx={{ fontSize: 11, color: '#374151', pl: 1.5 }}>• {item}</Typography>
                            ))}
                          </Stack>
                        ) : (
                          <Typography sx={{ fontSize: 11, color: '#64748b', fontStyle: 'italic', pl: 1.5 }}>
                            Syllabus completed - Focus on revision
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </Stack>
          </CardContent>
        </Card>

        <Card sx={{ bgcolor: '#fff', borderRadius: 2, mb: 2, boxShadow: 1 }}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <School sx={{ fontSize: 22, color: '#2563eb' }} />
              <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#1a1a1a', flex: 1 }}>
                Student Overview
              </Typography>
              <Button variant="outlined" size="small" onClick={handleSelectAllStudents} sx={{ fontSize: 11, textTransform: 'none' }}>
                {selectedStudents.length === students.length ? 'Deselect All' : 'Select All'}
              </Button>
              <Button variant="contained" size="small" startIcon={<AutoAwesome sx={{ fontSize: 14 }} />} onClick={() => handleOpenTestDialog('multiple', 'all', selectedStudents)} disabled={selectedStudents.length === 0} sx={{ fontSize: 12, textTransform: 'none' }}>
                Generate Test ({selectedStudents.length})
              </Button>
            </Box>
            <TableContainer sx={{ maxHeight: 500 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" sx={{ bgcolor: '#f8fafc', fontSize: 12, fontWeight: 600 }}></TableCell>
                    <TableCell sx={{ bgcolor: '#f8fafc', fontSize: 12, fontWeight: 600 }}>Roll No</TableCell>
                    <TableCell sx={{ bgcolor: '#f8fafc', fontSize: 12, fontWeight: 600 }}>Student</TableCell>
                    <TableCell sx={{ bgcolor: '#f8fafc', fontSize: 12, fontWeight: 600 }}>Strong Topics</TableCell>
                    <TableCell sx={{ bgcolor: '#f8fafc', fontSize: 12, fontWeight: 600 }}>Average Topics</TableCell>
                    <TableCell sx={{ bgcolor: '#f8fafc', fontSize: 12, fontWeight: 600 }}>Focus Areas</TableCell>
                    <TableCell sx={{ bgcolor: '#f8fafc', fontSize: 12, fontWeight: 600, textAlign: 'center' }}>Grade</TableCell>
                    <TableCell sx={{ bgcolor: '#f8fafc', fontSize: 12, fontWeight: 600, textAlign: 'center' }}>Score</TableCell>
                    <TableCell sx={{ bgcolor: '#f8fafc', fontSize: 12, fontWeight: 600, textAlign: 'center' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {students.map((student, index) => {
                    const gradeColor = getGradeColor(student.avgGrade);
                    const avatarColor = getAvatarColor(index);
                    const isExpanded = expandedStudentTests === student.id;
                    return (
                      <React.Fragment key={student.id}>
                        <TableRow sx={{ '&:hover': { bgcolor: '#f8fafc' }, cursor: 'pointer' }}>
                          <TableCell padding="checkbox">
                            <Checkbox size="small" checked={selectedStudents.includes(student.id)} onChange={() => handleStudentSelect(student.id)} />
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#1a1a1a' }}>{student.rollNo}</Typography>
                          </TableCell>
                          <TableCell onClick={() => handleStudentRowClick(student.id)}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar sx={{ width: 32, height: 32, bgcolor: avatarColor.bg, color: avatarColor.text, fontSize: 11, fontWeight: 600 }}>{getInitials(student.name)}</Avatar>
                              <Box>
                                <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#1a1a1a' }}>{student.name}</Typography>
                                <Typography sx={{ fontSize: 10, color: '#64748b' }}>{student.testsCompleted} tests</Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {student.strongTopics.length > 0 ? student.strongTopics.slice(0, 3).map((topic, i) => (
                                <Chip key={i} label={topic} size="small" sx={{ fontSize: 9, bgcolor: '#dcfce7', color: '#166534' }} />
                              )) : <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>—</Typography>}
                              {student.strongTopics.length > 3 && <Chip label={`+${student.strongTopics.length - 3}`} size="small" sx={{ fontSize: 9, bgcolor: '#dcfce7', color: '#166534' }} />}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {student.averageSubtopics.length > 0 ? student.averageSubtopics.slice(0, 2).map((topic, i) => (
                                <Chip key={i} label={topic} size="small" sx={{ fontSize: 9, bgcolor: '#fef3c7', color: '#92400e' }} />
                              )) : <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>—</Typography>}
                              {student.averageSubtopics.length > 2 && <Chip label={`+${student.averageSubtopics.length - 2}`} size="small" sx={{ fontSize: 9, bgcolor: '#fef3c7', color: '#92400e' }} />}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {student.focusAreas.length > 0 ? student.focusAreas.slice(0, 2).map((area, i) => (
                                <Chip key={i} label={area} size="small" sx={{ fontSize: 9, bgcolor: '#fee2e2', color: '#991b1b' }} />
                              )) : <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>—</Typography>}
                              {student.focusAreas.length > 2 && <Chip label={`+${student.focusAreas.length - 2}`} size="small" sx={{ fontSize: 9, bgcolor: '#fee2e2', color: '#991b1b' }} />}
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Chip label={student.avgGrade} size="small" sx={{ fontSize: 11, fontWeight: 600, bgcolor: gradeColor.bg, color: gradeColor.text }} />
                          </TableCell>
                          <TableCell align="center">
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#1a1a1a' }}>{student.overallScore}%</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Button variant="outlined" size="small" startIcon={<Send sx={{ fontSize: 12 }} />} onClick={() => handleOpenTestDialog('individual', student.gradeCategory, [student.id])} sx={{ fontSize: 11, textTransform: 'none' }}>
                              Test
                            </Button>
                          </TableCell>
                        </TableRow>
                        {isExpanded && (
                          <TableRow>
                            <TableCell colSpan={9} sx={{ bgcolor: '#f8fafc', p: 2 }}>
                              <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a', mb: 1.5 }}>
                                Test History - {student.name} ({student.rollNo})
                              </Typography>
                              <Grid container spacing={1}>
                                {currentSubjectData.tests.map((test) => {
                                  const attempted = student.attemptedTests.includes(test.id);
                                  const score = student.testScores[test.id];
                                  return (
                                    <Grid item xs={12} sm={6} md={4} key={test.id}>
                                      <Paper sx={{ p: 1.2, bgcolor: attempted ? alpha('#22c55e', 0.05) : alpha('#ef4444', 0.05), borderRadius: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.5 }}>
                                          {attempted ? <CheckCircle sx={{ fontSize: 16, color: '#22c55e' }} /> : <Cancel sx={{ fontSize: 16, color: '#ef4444' }} />}
                                          <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#1a1a1a', flex: 1 }}>{test.name}</Typography>
                                        </Box>
                                        <Typography sx={{ fontSize: 10, color: '#64748b', mb: 0.3 }}>{test.topic} • {test.subtopic}</Typography>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                          <Typography sx={{ fontSize: 10, color: '#64748b' }}>{test.date}</Typography>
                                          {attempted && <Chip label={`${score}%`} size="small" sx={{ fontSize: 9, fontWeight: 600, bgcolor: score >= 85 ? '#22c55e' : score >= 70 ? '#f59e0b' : '#ef4444', color: '#fff' }} />}
                                        </Box>
                                      </Paper>
                                    </Grid>
                                  );
                                })}
                              </Grid>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        <Card sx={{ bgcolor: '#fff', borderRadius: 2, mb: 2, boxShadow: 1 }}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Psychology sx={{ fontSize: 22, color: '#8b5cf6' }} />
              <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#1a1a1a', flex: 1 }}>
                Student Concept Overview
              </Typography>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel sx={{ fontSize: 12 }}>Select Student</InputLabel>
                <Select value={selectedStudentForConcept || ''} label="Select Student" onChange={(e) => setSelectedStudentForConcept(e.target.value)} sx={{ fontSize: 12 }}>
                  {students.map((student) => (
                    <MenuItem key={student.id} value={student.id} sx={{ fontSize: 12 }}>{student.rollNo} - {student.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            {selectedStudentForConcept ? (
              <Box>
                {(() => {
                  const student = students.find(s => s.id === selectedStudentForConcept);
                  return (
                    <>
                      <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f8fafc', borderRadius: 1 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={6} sm={3}>
                            <Typography sx={{ fontSize: 11, color: '#64748b', mb: 0.3 }}>Roll Number</Typography>
                            <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a' }}>{student.rollNo}</Typography>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Typography sx={{ fontSize: 11, color: '#64748b', mb: 0.3 }}>Overall Score</Typography>
                            <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a' }}>{student.overallScore}%</Typography>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Typography sx={{ fontSize: 11, color: '#64748b', mb: 0.3 }}>Tests Completed</Typography>
                            <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a' }}>{student.testsCompleted}</Typography>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Typography sx={{ fontSize: 11, color: '#64748b', mb: 0.3 }}>Grade</Typography>
                            <Chip label={student.avgGrade} size="small" sx={{ fontSize: 12, fontWeight: 600, ...getGradeColor(student.avgGrade) }} />
                          </Grid>
                        </Grid>
                      </Box>
                      <TableContainer sx={{ maxHeight: 400 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ bgcolor: '#f8fafc', fontSize: 12, fontWeight: 600 }}>Topic</TableCell>
                              <TableCell sx={{ bgcolor: '#f8fafc', fontSize: 12, fontWeight: 600 }}>Subtopic</TableCell>
                              <TableCell sx={{ bgcolor: '#f8fafc', fontSize: 12, fontWeight: 600, textAlign: 'center' }}>Concepts Read</TableCell>
                              <TableCell sx={{ bgcolor: '#f8fafc', fontSize: 12, fontWeight: 600, textAlign: 'center' }}>Tests Given</TableCell>
                              <TableCell sx={{ bgcolor: '#f8fafc', fontSize: 12, fontWeight: 600, textAlign: 'center' }}>Avg Score</TableCell>
                              <TableCell sx={{ bgcolor: '#f8fafc', fontSize: 12, fontWeight: 600 }}>Performance</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {student.conceptMastery.map((concept, idx) => (
                              <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                <TableCell><Typography sx={{ fontSize: 11, fontWeight: 600, color: '#1a1a1a' }}>{concept.topic}</Typography></TableCell>
                                <TableCell><Typography sx={{ fontSize: 11, color: '#64748b' }}>{concept.subtopic}</Typography></TableCell>
                                <TableCell align="center">
                                  <Typography sx={{ fontSize: 11, color: '#1a1a1a' }}>{concept.conceptsRead}</Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Typography sx={{ fontSize: 11, color: '#1a1a1a' }}>{concept.testsGiven}</Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#1a1a1a' }}>
                                    {concept.testsGiven > 0 ? `${concept.avgScore}%` : 'N/A'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ width: '100%' }}>
                                    <LinearProgress 
                                      variant="determinate" 
                                      value={concept.testsGiven > 0 ? concept.avgScore : 0} 
                                      sx={{ 
                                        height: 8, 
                                        borderRadius: 4,
                                        bgcolor: '#f1f5f9',
                                        '& .MuiLinearProgress-bar': { 
                                          borderRadius: 4,
                                          bgcolor: concept.testsGiven === 0 ? '#94a3b8' : concept.avgScore >= 85 ? '#22c55e' : concept.avgScore >= 70 ? '#f59e0b' : '#ef4444'
                                        }
                                      }} 
                                    />
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  );
                })()}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Psychology sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                <Typography sx={{ fontSize: 13, color: '#64748b' }}>Select a student to view their concept mastery details</Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        <Card sx={{ bgcolor: '#fff', borderRadius: 2, boxShadow: 1 }}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <EmojiEvents sx={{ fontSize: 22, color: '#f59e0b' }} />
              <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#1a1a1a' }}>Class Category Performance</Typography>
            </Box>
            <Grid container spacing={2}>
              {[
                { key: 'excellent', label: 'Excellent', color: '#22c55e', icon: Star },
                { key: 'good', label: 'Good', color: '#3b82f6', icon: TrendingUp },
                { key: 'average', label: 'Average', color: '#f59e0b', icon: BarChart },
                { key: 'focus', label: 'Focus Students', color: '#ef4444', icon: Warning }
              ].map(({ key, label, color, icon: Icon }) => (
                <Grid item xs={12} sm={6} md={3} key={key}>
                  <Paper sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a', display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Icon sx={{ fontSize: 18, color }} /> {label} ({classCategories[key].length})
                      </Typography>
                      <Button variant="contained" size="small" onClick={() => handleOpenTestDialog('category', key, classCategories[key].map(s => s.id))} sx={{ fontSize: 10, px: 1.2, bgcolor: color, textTransform: 'none', color: '#fff' }}>Test</Button>
                    </Box>
                    <Stack spacing={0.8} sx={{ maxHeight: 160, overflowY: 'auto' }}>
                      {classCategories[key].slice(0, 5).map((student, idx) => {
                        const avatarColor = getAvatarColor(students.indexOf(student));
                        return (
                          <Box key={student.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 0.8, bgcolor: '#fff', borderRadius: 1 }}>
                            <Avatar sx={{ width: 24, height: 24, bgcolor: avatarColor.bg, color: avatarColor.text, fontSize: 10 }}>{getInitials(student.name)}</Avatar>
                            <Box sx={{ flex: 1 }}>
                              <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#1a1a1a' }}>{student.name}</Typography>
                              <Typography sx={{ fontSize: 9, color: '#64748b' }}>{student.rollNo}</Typography>
                            </Box>
                            <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#1a1a1a' }}>{student.avgGrade}</Typography>
                          </Box>
                        );
                      })}
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      </Box>

      <Dialog open={openTestDialog} onClose={() => setOpenTestDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 600 }}>Generate CBSE Test Paper</Typography>
          <IconButton size="small" onClick={() => setOpenTestDialog(false)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1, color: '#64748b' }}>Performance Type (Multiple Select)</Typography>
              <Stack spacing={0.8}>
                {['Strong Topics', 'Average Subtopics', 'Focus Areas', 'Mixed'].map((type) => (
                  <FormControlLabel key={type} control={<Checkbox size="small" checked={selectedPerformanceType.includes(type)} onChange={(e) => { if (e.target.checked) { setSelectedPerformanceType([...selectedPerformanceType, type]); } else { setSelectedPerformanceType(selectedPerformanceType.filter(t => t !== type)); } }} />} label={<Typography sx={{ fontSize: 12 }}>{type}</Typography>} />
                ))}
              </Stack>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1, color: '#64748b' }}>Selected Students ({selectedStudents.length})</Typography>
              <Paper sx={{ p: 1.5, maxHeight: 180, overflowY: 'auto', bgcolor: '#f8fafc' }}>
                {selectedStudents.length > 0 ? <Stack spacing={0.8}>{students.filter(s => selectedStudents.includes(s.id)).map(student => <Typography key={student.id} sx={{ fontSize: 12, color: '#374151' }}>• {student.rollNo} - {student.name} ({student.avgGrade})</Typography>)}</Stack> : <Typography sx={{ fontSize: 12, color: '#64748b', textAlign: 'center' }}>No students selected</Typography>}
              </Paper>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenTestDialog(false)} sx={{ fontSize: 12, textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleGenerateTest} disabled={selectedStudents.length === 0 || selectedPerformanceType.length === 0} startIcon={<PlayArrow />} sx={{ fontSize: 12, textTransform: 'none' }}>Generate Test</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompleteTeacherDashboard;